import { Fragment, memo, useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { DeliveryErrorTooltip } from '@/components/atoms/DeliveryErrorTooltip/DeliveryErrorTooltip';
import { cn } from '@/lib/utils';
import { Message as MessageType, ConversationSource } from '@/shared/types/chat';

import Message from '../Message';
import MessageStatus from '../MessageStatus/MessageStatus';

type Props = {
  message: MessageType;
  showTime: boolean;
  showAvatar: boolean;
  source: ConversationSource;
};

export const MessageGroup = memo(({ message, showTime, showAvatar, source }: Props) => {
  const { failed_at, author_type, error_code } = message;
  const { t } = useTranslation();
  const [attachmentError, setAttachmentError] = useState<Error | null>(null);
  const handleAttachmentError = useCallback(
    (error?: Error) => {
      setAttachmentError(error ?? new Error(t('conversation.attachment_error')));
    },
    [t]
  );

  const failureLabel = (
    <span className="text-sm leading-5 font-normal text-destructive">
      {failed_at ? t('conversation.message_failed') : attachmentError?.message}
    </span>
  );

  const failed = Boolean(failed_at || attachmentError);

  return (
    <Fragment key={message.message_id}>
      <Message
        message={message}
        showAvatar={showAvatar}
        group={showTime}
        source={source}
        handleAttachmentError={handleAttachmentError}
      />
      {failed ? (
        <div
          className={cn(
            'mt-1 mb-3 flex items-center',
            author_type === 'visitor' ? 'justify-start' : 'mr-8 justify-end'
          )}
        >
          <DeliveryErrorTooltip errorCode={failed_at ? error_code : undefined}>
            {failureLabel}
          </DeliveryErrorTooltip>
        </div>
      ) : (
        showTime && (
          <MessageStatus created={message.created} authorType={author_type} shouldShow={showTime} />
        )
      )}
    </Fragment>
  );
});

MessageGroup.displayName = 'MessageGroup';
